import appInfo from "@common/appInfo";
import { IconType, System, SystemColor } from "@common/declarations";
import { urls } from "@common/hooks/navigation";
import { formatMoneyDisplay } from "@common/lib/format";
import { type I18n, i18nFormat } from "@common/lib/i18n";
import { joinDefined } from "@common/lib/misc";
import { type Account } from "@common/modules/account/types";
import { isNull } from "lodash";
import { type ActivityItem } from "./component";

export const getTransactionItemIcon = (item: Account.Api.AccountTransaction): any =>
  item.accountTransactionStatusIsSuccessful
    ? item.accountTransactionTypeAdjustment === "+"
      ? IconType.ArrowDown
      : IconType.ArrowUp
    : IconType.Cross;

const addKey = (keys: string[], append: string | null) => {
  if (!append) return;

  for (let i = 0, length = keys.length; i < length; i++) {
    keys.push(`${keys[i]}_${append}`);
  }
};

const getHistoryKey = (
  i18n: I18n,
  item: System.Api.HistoryItem,
  showLink: boolean,
  isTitle: boolean,
  labelKeyMapper?: (labelKey: string) => string
): string => {
  let baseKey = `historyType`;
  if (item.orderId) {
    baseKey = `${baseKey}_Order`;
  } else if (item.subscriptionId) {
    baseKey = `${baseKey}_Subscription`;
  } else if (item.recruitUserId) {
    baseKey = `${baseKey}_Network`;
  }

  baseKey = `${baseKey}_${isTitle ? "Title" : "Description"}`;

  const isAdmin = appInfo.user?.isStaff || appInfo.user?.isSuperUser;

  const userAppend =
    isNull(item.createdByUserIsContact) || !isAdmin ? null : item.createdByUserIsContact ? "Customer" : "Staff";
  const linkAppend = isAdmin && showLink ? "WithLink" : null;
  let typeAppend = item.historyTypeKey.replace("Order", "").replace("Subscription", "").replace("Network", "");

  if (item.historyTypeKey === "OrderPayment" && item.orderPaymentAuthorizedAmount) {
    // special handling
    const paymentAmount = item.orderPaymentAmount!;
    const paymentAuthorizedAmount = item.orderPaymentAuthorizedAmount!;

    if (!paymentAmount) {
      typeAppend = "PaymentAuthorizationCanceled"; // cancel
    } else if (paymentAmount === paymentAuthorizedAmount) {
      typeAppend = "PaymentCaptured"; // full capture
    } else {
      typeAppend = "PaymentCapturedPartial"; // partial capture
    }
  }

  const keys: string[] = [baseKey];

  addKey(keys, userAppend);
  addKey(keys, linkAppend);
  addKey(keys, typeAppend);

  if (item.recruitUserId) {
    addKey(keys, item.userId === item.createdByUserId || !item.createdByUserId ? "Direct" : "Indirect");
  }

  const mappedKeys = keys.reverse().map((x) => (labelKeyMapper ? labelKeyMapper(x) : x));

  const key = mappedKeys.find((x) => x in i18n)!;
  return key ?? mappedKeys[0];
};

const i18nFormatHistory = (
  i18n: I18n,
  key: string,
  item: System.Api.HistoryItem,
  showLink: boolean,
  obfuscateAmounts?: boolean
): string | null => {
  const res = i18nFormat(
    i18n[key],
    ["USER", joinDefined(" ", item.createdByUserFirstName, item.createdByUserLastName)],
    ["NOTE", item.description],
    ["STEP", item.orderFlowStepName],
    [
      "AMOUNT",
      formatMoneyDisplay(item.orderPaymentAmount, item.orderCurrencyCode!, {
        obfuscate: obfuscateAmounts
      })
    ],
    [
      "AUTHORIZED_AMOUNT",
      formatMoneyDisplay(item.orderPaymentAuthorizedAmount, item.orderCurrencyCode!, {
        obfuscate: obfuscateAmounts
      })
    ],
    ["PAYMENT_METHOD", item.orderPaymentVoucherCodeId ? i18n.voucher : item.paymentMethodName],
    [
      "ORDER",
      showLink && urls.order.detail ? `<a href="${urls.order.detail(item.orderId!)}">#${item.orderNumber}</a>` : ""
    ],
    ["SOURCE", i18n.orderStatus.toLowerCase()],
    [
      "SUBSCRIPTION",
      showLink && urls.subscription.detail
        ? `<a href="${urls.subscription.detail(item.subscriptionId!)}">#${item.subscriptionNumber}</a>`
        : ""
    ],
    ["TARGET", item.historyTypeKey],
    ["RECRUIT", joinDefined(" ", item.recruitUserFirstName, item.recruitUserLastName)]
  );

  return key.endsWith("PaymentCapturedPartial")
    ? i18nFormat(res, [
        "RELEASED_AMOUNT",
        formatMoneyDisplay(
          (item.orderPaymentAuthorizedAmount ?? 0) - (item.orderPaymentAmount ?? 0),
          item.orderCurrencyCode!,
          {
            obfuscate: obfuscateAmounts
          }
        )
      ])
    : res;
};

export const mapHistoryItem = (
  i18n: I18n,
  item: System.Api.HistoryItem,
  showLink: boolean,
  obfuscateAmounts?: boolean,
  labelKeyMapper?: (labelKey: string) => string
): ActivityItem => {
  const titleKey = getHistoryKey(i18n, item, showLink, true, labelKeyMapper);

  const descriptionKey = getHistoryKey(i18n, item, showLink, false, labelKeyMapper);

  const isPaymentAuthorizationCanceled = descriptionKey.endsWith("PaymentAuthorizationCanceled");

  return {
    id: item.createdOnDate,
    icon: isPaymentAuthorizationCanceled ? IconType.Undo : (item.historyTypeIcon as IconType),
    color: isPaymentAuthorizationCanceled ? SystemColor.Red : (item.historyTypeColor as SystemColor),
    title: i18nFormatHistory(i18n, titleKey, item, showLink, obfuscateAmounts),
    description: i18nFormatHistory(i18n, descriptionKey, item, showLink, obfuscateAmounts),
    date: item.createdOnDate
  };
};
